import { useEffect, useRef } from 'react';

// Derive the WebSocket URL from the current page origin so the app works over
// plain http (ws://) while nginx has no TLS and over https (wss://) once a
// cert is installed — without a rebuild. nginx proxies /websocket to the
// backend on :3002.
function getBaseWsUrl(): string {
  if (typeof window === 'undefined') return '';
  const scheme = window.location.protocol === 'https:' ? 'wss' : 'ws';
  return `${scheme}://${window.location.host}/websocket`;
}

interface MessageObject<T> {
  channel: string;
  message: T;
}

// Reconnect backoff: start fast, cap at 10s. The backend is typically a
// sub-second restart; anything slower almost always means the host is down
// and we want to keep retrying forever so the page "just heals" when it
// comes back without requiring a manual refresh.
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS  = 10_000;

/**
 * Callback-based WebSocket hook. Subscribes to the given channels and invokes
 * `onMessage` for every server push. Unlike `useWebSocket`, this hook does
 * NOT accumulate messages in state, so it is safe to use on long-lived
 * pages (e.g. the organizer live board) without memory growth or
 * unnecessary re-renders.
 *
 * The handler is read through a ref so updating it between renders does not
 * force socket reconnection.
 *
 * The socket auto-reconnects with exponential backoff when it drops. This
 * matters during dev (backend restarts are frequent) and in production
 * whenever the backend is bounced — without reconnect, a long-lived tab
 * silently stops receiving updates until the user hard-refreshes, which
 * has been seen to produce "I added X and the live table didn't change"
 * bug reports.
 */
export default function useWebSocketHandler<T = unknown>(
  channels: string[],
  onMessage: (msg: MessageObject<T>) => void,
  action: string = 'subscribe',
): void {
  const handlerRef = useRef(onMessage);
  handlerRef.current = onMessage;

  const channelsKey = channels.join('|');

  useEffect(() => {
    const params = new URLSearchParams();
    params.append('action', action);
    channels.forEach((c) => params.append('channel', c));
    const wsUrl = `${getBaseWsUrl()}?${params.toString()}`;

    let cancelled = false;
    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    const scheduleReconnect = () => {
      if (cancelled) return;
      const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_BASE_MS * 2 ** attempt);
      attempt += 1;
      reconnectTimer = setTimeout(connect, delay);
    };

    const connect = () => {
      if (cancelled) return;
      const s = new WebSocket(wsUrl);
      socket = s;

      s.addEventListener('open', () => {
        attempt = 0; // healthy connection resets the backoff
      });

      s.addEventListener('message', (event: MessageEvent) => {
        try {
          const parsed: MessageObject<T> = JSON.parse(event.data);
          handlerRef.current(parsed);
        } catch (err) {
          console.error('useWebSocketHandler: malformed message', err);
        }
      });

      s.addEventListener('close', () => {
        if (cancelled) return;
        scheduleReconnect();
      });

      // `error` fires before `close` for most failures; no-op here because
      // the `close` handler triggers the reconnect. Logging once is enough
      // to make bad URLs / server-side refusals visible in devtools.
      s.addEventListener('error', () => {
        // intentionally empty — close handler will reconnect
      });
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer != null) clearTimeout(reconnectTimer);
      if (socket && socket.readyState <= WebSocket.OPEN) socket.close();
    };
    // channelsKey is the stable key; `action` is a string and never changes in practice.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelsKey, action]);
}
